import { useEffect, useState } from 'react';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { useQueryClient } from '@tanstack/react-query';

import type { Appointment } from '@/react-query/useAppointments';
import { useCreateReferralByPatientId } from '@/react-query/useCreateReferralByPatientId';
import type { PatientSearchResult } from '@/react-query/usePatientSearch';
import {
  useDeleteReferralById,
  useReferralDetails,
  useReferralInfo,
} from '@/react-query/useReferralInfo';
import { useAppContext } from '@/utils/hooks/useAppContext';
import { parseDate } from '@/utils/parseDate';

import { ReferralForm } from './components/ReferralForm';

interface ReferralModalProps {
  appointment?: Appointment;
  patientData?: PatientSearchResult;
  unifiedAppointmentId?: string;
  isOpen: boolean;
  onClose: () => void;
  hideDownloadButton?: boolean;
}

export const ReferralModal = ({
  appointment,
  patientData,
  unifiedAppointmentId: propsUnifiedAppointmentId,
  isOpen,
  onClose,
  hideDownloadButton = false,
}: ReferralModalProps) => {
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  const [createdReferralId, setCreatedReferralId] = useState<string | null>(
    null,
  );
  const [isCreatingReferral, setIsCreatingReferral] = useState(false);
  const [isReferralSubmitted, setIsReferralSubmitted] = useState(false);

  // Determine if we're in appointment mode or patient-only mode
  const hasAppointment = !!appointment;

  // Use unifiedAppointmentId from props or appointment
  const unifiedAppointmentId =
    propsUnifiedAppointmentId || appointment?.appointment?.unifiedId;

  // Get patient videaIds for API calls
  const pmsPatientVideaIdForApi = appointment?.patient?.pmsPatientVideaId
    ? appointment.patient.pmsPatientVideaId
    : patientData?.pmsPatientVideaId || undefined;

  // For patient search mode, use the videaId from patientData
  const imagingPatientVideaIdForApi = appointment?.patient
    ?.imagingPatientVideaId
    ? appointment.patient.imagingPatientVideaId
    : patientData?.imagingPatientVideaId || undefined;

  // Determine if we should fetch practice info based on having patient IDs
  const hasPatientIds = !!(
    pmsPatientVideaIdForApi || imagingPatientVideaIdForApi
  );
  const shouldFetchPracticeInfo =
    isOpen && !!user?.primaryPracticeId && hasPatientIds;

  // Get practice and patient information whenever we have patient IDs
  const { data: practiceInfo, isLoading: isLoadingPracticeInfo } =
    useReferralInfo(
      user?.primaryPracticeId || '',
      pmsPatientVideaIdForApi,
      imagingPatientVideaIdForApi,
      {
        enabled: shouldFetchPracticeInfo,
      },
    );

  const { mutate: createReferral } = useCreateReferralByPatientId();
  const { mutate: deleteReferralById } = useDeleteReferralById();

  // Get existing referral details (if we have a unified appointment ID)
  const shouldFetchReferralDetails = isOpen && !!unifiedAppointmentId;
  const { data: referralInfo, isLoading: isLoadingReferralInfo } =
    useReferralDetails(unifiedAppointmentId || '', {
      enabled: shouldFetchReferralDetails,
    });

  useEffect(() => {
    if (isOpen && !createdReferralId && !isCreatingReferral) {
      setIsCreatingReferral(true);

      createReferral(
        {
          practiceId: String(user?.primaryPracticeId || ''),
          ...(unifiedAppointmentId && { unifiedAppointmentId }),
          ...(pmsPatientVideaIdForApi && {
            pmsPatientVideaId: pmsPatientVideaIdForApi,
          }),
          ...(imagingPatientVideaIdForApi && {
            imagingPatientVideaId: imagingPatientVideaIdForApi,
          }),
          status: 'in_progress',
        },
        {
          onSuccess: (data) => {
            setCreatedReferralId(data.id);
            setIsCreatingReferral(false);
          },
          onError: (error) => {
            console.error('Failed to create referral:', error);
            setIsCreatingReferral(false);
          },
        },
      );
    }

    if (!isOpen) {
      setCreatedReferralId(null);
      setIsCreatingReferral(false);
      setIsReferralSubmitted(false);
    }
  }, [
    isOpen,
    createdReferralId,
    isCreatingReferral,
    createReferral,
    user?.primaryPracticeId,
    unifiedAppointmentId,
    pmsPatientVideaIdForApi,
    imagingPatientVideaIdForApi,
  ]);

  const isLoading =
    isCreatingReferral ||
    (shouldFetchPracticeInfo && isLoadingPracticeInfo) ||
    (shouldFetchReferralDetails && isLoadingReferralInfo);

  // Parse patient date of birth from practice info
  const parsePatientDOB = () => {
    if (practiceInfo?.patient?.dateOfBirth) {
      const dob = parseDate(practiceInfo.patient.dateOfBirth);
      return {
        day: dob.getDate().toString().padStart(2, '0'),
        month: (dob.getMonth() + 1).toString().padStart(2, '0'),
        year: dob.getFullYear().toString(),
      };
    }
    return { day: '', month: '', year: '' };
  };

  const patientDOB = parsePatientDOB();

  const handleClose = () => {
    // Delete referral if it was created but not submitted
    if (createdReferralId && !isReferralSubmitted) {
      deleteReferralById(createdReferralId, {
        onSuccess: () => {
          queryClient.invalidateQueries(['referrals']);
          onClose();
        },
        onError: (error) => {
          console.error('Failed to delete referral:', error);
          queryClient.invalidateQueries(['referrals']);
          onClose();
        },
      });
    } else {
      queryClient.invalidateQueries(['referrals']);
      onClose();
    }
  };

  const handleSubmitSuccess = () => {
    // Optimistically mark referral as submitted
    setIsReferralSubmitted(true);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent className="vai-max-w-[1000px] vai-p-4">
        <DialogHeader className="vai-pb-2">
          <DialogTitle>
            <Trans>Send Referral</Trans>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="vai-flex vai-items-center vai-justify-center vai-h-[400px]">
            <FontAwesomeIcon
              icon={faSpinner}
              className="vai-h-8 vai-w-8 vai-text-primary vai-animate-spin"
            />
          </div>
        ) : (
          <ReferralForm
            onCancel={handleClose}
            isLoading={isLoading}
            patientId={imagingPatientVideaIdForApi || ''}
            patientVideaId={
              patientData?.imagingPatientVideaId ||
              appointment?.patient?.videaId ||
              undefined
            }
            videaPracticeId={String(user?.primaryPracticeId || '')}
            mode={hasAppointment ? 'appointment' : 'patient'}
            pmsPatientVideaId={pmsPatientVideaIdForApi}
            imagingPatientVideaId={imagingPatientVideaIdForApi}
            referralId={createdReferralId || undefined}
            onSubmitSuccess={handleSubmitSuccess}
            defaultValues={{
              patientFirstName:
                referralInfo?.patient_first_name ||
                practiceInfo?.patient?.firstName ||
                patientData?.name?.split(' ')[0] ||
                '',
              patientLastName:
                referralInfo?.patient_last_name ||
                practiceInfo?.patient?.lastName ||
                patientData?.name?.split(' ').slice(1).join(' ') ||
                '',
              patientEmail:
                referralInfo?.patient_email ||
                practiceInfo?.patient?.email ||
                '',
              patientPhone:
                referralInfo?.patient_phone ||
                practiceInfo?.patient?.phone ||
                '',
              patientDOBYear: patientDOB.year,
              patientDOBMonth: patientDOB.month,
              patientDOBDay: patientDOB.day,
              doctorFirstName: practiceInfo?.provider?.firstName || '',
              doctorLastName: practiceInfo?.provider?.lastName || '',
              practiceEmail: practiceInfo?.practiceEmail || '',
              doctorPhone: practiceInfo?.practicePhoneNumber || '',
              specialtyId: referralInfo?.specialty_id,
              specialtyName: referralInfo?.specialty_name,
              proceduresIds: referralInfo?.procedures_ids || [],
              referralPracticeId: referralInfo?.referral_practice_id,
              referralSpecialistId: referralInfo?.referral_specialist_id,
            }}
            appointment={appointment}
            unifiedAppointmentId={unifiedAppointmentId}
            onClose={handleClose}
            hideDownloadButton={hideDownloadButton}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
